/* Owns the health strip of PANEL.md §4 / SPEC §9: one line under the topbar on
   every `/s/:serverId/*` route, driven by `qk.health(serverId)` and refreshed by
   the `server.status` and `config.updated` WS messages.

   The five variants are evaluated first-match-wins and the order is deliberate:
   an IP lock stops the agent authenticating at all, so it outranks "offline",
   which in turn outranks the two advisory states. Only one line is ever shown,
   because a strip that stacks warnings stops being read. */

import { AlertTriangle, RefreshCw, ShieldAlert, Wifi, WifiOff } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { Button } from '../components/ui/Button.js'
import { cn } from '../components/ui/cn.js'
import { pushToast } from '../components/ui/Toast.js'
import { useLang, useT } from '../i18n/index.js'
import { ApiHttpError } from '../lib/api.js'
import { formatDuration, formatRelative } from '../lib/format.js'
import { qk } from '../lib/queryKeys.js'
import { useCan } from '../lib/permissions.js'
import { trackIssuedCommand } from '../ws/useServerFeed.js'
import { useConnectionChip } from '../ws/useConnectionState.js'
import { healthQueryOptions, requestConfigReload, resetLicenseIp, serverQueryOptions } from './api.js'

type StripTone = 'ok' | 'warn' | 'danger'

const TONE_CLASS: Record<StripTone, string> = {
  ok: 'border-ok/40 bg-ok/10 text-text',
  warn: 'border-warn/40 bg-warn/10 text-text',
  danger: 'border-danger/40 bg-danger/10 text-text',
}

const DOT_CLASS: Record<StripTone, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  danger: 'bg-danger',
}

export interface StatusStripProps {
  serverId: string
}

export function StatusStrip({ serverId }: StatusStripProps): ReactNode {
  const t = useT()
  const { lang } = useLang()
  const client = useQueryClient()
  const health = useQuery(healthQueryOptions(serverId))
  const server = useQuery(serverQueryOptions(serverId))
  const canReload = useCan('commands.reload', serverId)
  const canManageLicense = useCan('license.manage', serverId)
  const chip = useConnectionChip()

  const reload = useMutation({
    mutationFn: () => requestConfigReload(serverId),
    onSuccess: (response) => {
      // Registering the id here is what lets `command.updated` toast the ack for
      // the operator who pressed the button and nobody else.
      trackIssuedCommand(response.command.id)
      pushToast({ variant: 'success', title: t('command.sent') })
      void client.invalidateQueries({ queryKey: ['commands', serverId, 'list'] })
    },
    onError: (error: unknown) => {
      pushToast({ variant: 'danger', title: toErrorTitle(error, t) })
    },
  })

  const resetIp = useMutation({
    mutationFn: (licenseId: string) => resetLicenseIp(serverId, licenseId),
    onSuccess: () => {
      pushToast({ variant: 'success', title: t('license.resetIp') })
      void client.invalidateQueries({ queryKey: qk.health(serverId) })
      void client.invalidateQueries({ queryKey: qk.licenses(serverId) })
    },
    onError: (error: unknown) => {
      // 3/day per license (API.md §1.6): the useful part of the refusal is when
      // the quota comes back, not that it was refused.
      const retryAfterMs =
        error instanceof ApiHttpError && typeof error.details?.['retryAfterMs'] === 'number'
          ? (error.details['retryAfterMs'] as number)
          : null
      pushToast({
        variant: 'danger',
        title: toErrorTitle(error, t),
        ...(retryAfterMs === null ? {} : { description: formatDuration(Math.round(retryAfterMs / 1000), lang) }),
      })
    },
  })

  if (!health.data) return null
  const data = health.data
  const licenseId = server.data?.license?.id ?? null
  const offline = data.status !== 'online'

  let tone: StripTone = 'ok'
  let message = ''
  let action: ReactNode = null

  if (data.license?.ipMismatch === true) {
    tone = 'danger'
    message = t('server.ipLock', { ip: data.license.boundIp ?? t('common.none') })
    action =
      canManageLicense && licenseId ? (
        <Button
          size="sm"
          variant="secondary"
          loading={resetIp.isPending}
          onClick={() => resetIp.mutate(licenseId)}
        >
          {t('server.resetIp')}
        </Button>
      ) : null
  } else if (offline) {
    tone = 'danger'
    message = t('server.offlineSince', { age: formatRelative(data.lastHeartbeatAt, lang) })
  } else if (data.agentOutdated) {
    tone = 'warn'
    message = t('server.outdated', {
      agentVersion: data.agentVersion ?? '—',
      minAgentVersion: data.minAgentVersion,
    })
    action = (
      <Link
        to={`/s/${serverId}/licenses`}
        className="rounded-md px-2 py-1 text-sm text-accent underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        {t('nav.licenses')}
      </Link>
    )
  } else if (!data.configSynced) {
    tone = 'warn'
    message = t('config.pendingSync', { applied: data.agentConfigVersion, current: data.configVersion })
    action = (
      <Button
        size="sm"
        variant="secondary"
        icon={RefreshCw}
        loading={reload.isPending}
        // An offline agent cannot pick up a command, so the control states why
        // rather than queueing work that will only ever expire.
        disabled={!canReload || offline}
        {...(canReload ? {} : { disabledReason: t('common.noPermission') })}
        onClick={() => reload.mutate()}
      >
        {t('server.reload')}
      </Button>
    )
  } else {
    tone = 'ok'
    message = t('server.onlineSummary', {
      players: data.playersOnline,
      max: data.maxPlayers,
      agentVersion: data.agentVersion ?? '—',
      configVersion: data.configVersion,
    })
  }

  const Icon = tone === 'ok' ? Wifi : tone === 'warn' ? AlertTriangle : ShieldAlert

  return (
    <div
      data-testid="status-strip"
      role="status"
      className={cn('flex flex-wrap items-center gap-3 border-b px-4 py-2 text-sm lg:px-6', TONE_CLASS[tone])}
    >
      <span aria-hidden className={cn('size-2 shrink-0 rounded-full lg:hidden', DOT_CLASS[tone])} />
      <Icon aria-hidden className="hidden size-4 shrink-0 lg:block" />
      <span className="min-w-0 flex-1 truncate">{message}</span>
      {action}
      <span className="flex items-center gap-1 text-xs text-text-muted">
        {chip.connection === 'open' ? (
          <Wifi aria-hidden className="size-3.5" />
        ) : (
          <WifiOff aria-hidden className="size-3.5" />
        )}
        {t(chip.labelKey)}
      </span>
      {chip.canReconnect ? (
        <Button size="sm" variant="ghost" onClick={chip.reconnect}>
          {t('server.reconnect')}
        </Button>
      ) : null}
    </div>
  )
}

/** Every panel error surface keys off the frozen `ErrorCode`, never off the message. */
function toErrorTitle(error: unknown, t: (key: string) => string): string {
  if (error instanceof ApiHttpError) return t(`errors.${error.code}`)
  return t('errors.INTERNAL')
}
